import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code2, Smartphone, Cloud, CheckCircle2, TrendingUp, Award, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { BannerSlider } from '../components/BannerSlider';
import { companyInfo, services, projects, testimonials, stats } from '../mock';

export const Home = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Banner Slider */}
      <BannerSlider />

      {/* Stats Section */}
      <section className="py-16 bg-[rgba(10,37,64,0.6)] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#00a8ff] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#4cd3ff] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center animate-on-scroll opacity-0 transition-all duration-700 hover:scale-110"
                style={{ 
                  transitionDelay: `${index * 100}ms`,
                  transform: 'translateY(30px)'
                }}
              >
                <div 
                  className="text-5xl md:text-6xl font-bold led-text mb-2 animate-pulse-scale"
                  style={{ textShadow: '0 0 20px #00a8ff, 0 0 40px #4cd3ff' }}
                >
                  {stat.value}
                </div>
                <div className="text-sm md:text-base text-[#00a8ff]" style={{ textShadow: '0 0 10px #00a8ff' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#00a8ff] rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#4cd3ff] rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16 animate-on-scroll opacity-0" style={{ transform: 'translateY(30px)' }}>
            <h2 
              className="text-4xl md:text-5xl font-bold tracking-wider mb-4 led-text relative inline-block animate-led-pulse"
              style={{ textShadow: '0 0 15px #00a8ff, 0 0 30px #4cd3ff' }}
            >
              OUR SERVICES
              <span className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-[#00a8ff]" style={{ boxShadow: '0 0 15px #00a8ff' }}></span>
            </h2>
            <p className="text-xl text-white mt-8" style={{ textShadow: '0 0 8px #00a8ff' }}>
              Comprehensive solutions tailored to your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon === 'Code2' ? Code2 : service.icon === 'Smartphone' ? Smartphone : Cloud;
              return (
                <Card
                  key={service.id}
                  className="animate-on-scroll opacity-0 group bg-[rgba(30,64,98,0.7)] border border-[#00a8ff] rounded-xl overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:shadow-[0_0_30px_#00a8ff] backdrop-blur-sm animate-float"
                  style={{ 
                    transitionDelay: `${index * 150}ms`,
                    transform: 'translateY(30px)',
                    animationDelay: `${index * 0.5}s`,
                    boxShadow: '0 0 15px rgba(0,168,255,0.3)'
                  }}
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-[#00a8ff] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" style={{ boxShadow: '0 0 10px #00a8ff' }}></div>
                  <CardContent className="p-8">
                    <div className="w-20 h-20 bg-[rgba(0,168,255,0.2)] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#00a8ff] transition-all duration-500 group-hover:rotate-12 group-hover:scale-110" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.5)' }}>
                      <IconComponent className="text-[#00a8ff] group-hover:text-[#0a2540] transition-colors duration-500" size={40} style={{ filter: 'drop-shadow(0 0 10px #00a8ff)' }} />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 led-text group-hover:text-white transition-colors" style={{ textShadow: '0 0 10px #00a8ff' }}>{service.title}</h3>
                    <p className="text-white mb-6" style={{ textShadow: '0 0 5px #00a8ff' }}>{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-[#00a8ff]" style={{ textShadow: '0 0 5px #00a8ff' }}>
                          <CheckCircle2 size={16} className="mr-2 flex-shrink-0" style={{ filter: 'drop-shadow(0 0 5px #00a8ff)' }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12 animate-on-scroll opacity-0" style={{ transform: 'translateY(30px)', transitionDelay: '600ms' }}>
            <Link to="/services">
              <Button className="bg-transparent border-2 border-[#00a8ff] text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] font-bold px-8 py-6 text-lg transition-all duration-300 group relative overflow-hidden" style={{ boxShadow: '0 0 20px #00a8ff', textShadow: '0 0 8px #00a8ff' }}>
                <span className="relative z-10 flex items-center gap-2">
                  View All Services
                  <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
                </span>
                <span className="absolute inset-0 bg-[#00a8ff] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></span>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-24 bg-[rgba(10,37,64,0.4)] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#00a8ff] rounded-full blur-3xl animate-pulse"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16 animate-on-scroll opacity-0" style={{ transform: 'translateY(30px)' }}>
            <h2 className="text-4xl md:text-5xl font-bold tracking-wider mb-4 led-text relative inline-block animate-led-pulse" style={{ textShadow: '0 0 15px #00a8ff, 0 0 30px #4cd3ff' }}>
              FEATURED PROJECTS
              <span className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-[#00a8ff]" style={{ boxShadow: '0 0 15px #00a8ff' }}></span>
            </h2>
            <p className="text-xl text-white mt-8" style={{ textShadow: '0 0 8px #00a8ff' }}>
              Recent work that showcases our expertise
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.slice(0, 3).map((project, index) => (
              <Card key={project.id} className="animate-on-scroll opacity-0 group overflow-hidden bg-[rgba(30,64,98,0.7)] border border-[#00a8ff] rounded-xl transition-all duration-500 hover:-translate-y-4" style={{ transitionDelay: `${index * 150}ms`, transform: 'translateY(30px)', boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}>
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                   width={1200} height={800} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a2540] via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
                  <div className="absolute inset-0 border-2 border-[#00a8ff] opacity-0 group-hover:opacity-100 transition-opacity duration-500 m-4" style={{ boxShadow: 'inset 0 0 20px #00a8ff' }}></div>
                </div>
                <CardContent className="p-6">
                  <div className="text-sm text-[#00a8ff] mb-2 font-bold" style={{ textShadow: '0 0 8px #00a8ff' }}>{project.category}</div>
                  <h3 className="text-xl font-bold mb-2 text-white group-hover:text-[#00a8ff] transition-colors" style={{ textShadow: '0 0 8px #00a8ff' }}>{project.title}</h3>
                  <p className="text-white text-sm" style={{ textShadow: '0 0 5px #00a8ff' }}>{project.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12 animate-on-scroll opacity-0" style={{ transform: 'translateY(30px)', transitionDelay: '500ms' }}>
            <Link to="/portfolio">
              <Button className="bg-[#00a8ff] text-[#0a2540] hover:bg-[#4cd3ff] font-bold px-8 py-6 text-lg transition-all duration-300 hover:scale-105 group" style={{ boxShadow: '0 0 20px #00a8ff' }}>
                <span className="flex items-center gap-2">
                  View All Projects
                  <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
                </span>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#4cd3ff] rounded-full blur-3xl animate-float"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16 animate-on-scroll opacity-0" style={{ transform: 'translateY(30px)' }}>
            <h2 className="text-4xl md:text-5xl font-bold tracking-wider mb-4 led-text relative inline-block animate-led-pulse" style={{ textShadow: '0 0 15px #00a8ff, 0 0 30px #4cd3ff' }}>
              CLIENT TESTIMONIALS
              <span className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-[#00a8ff]" style={{ boxShadow: '0 0 15px #00a8ff' }}></span>
            </h2>
            <p className="text-xl text-white mt-8" style={{ textShadow: '0 0 8px #00a8ff' }}>
              What our clients say about working with us
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.slice(0, 2).map((testimonial, index) => (
              <Card key={testimonial.id} className="animate-on-scroll opacity-0 bg-[rgba(30,64,98,0.7)] border border-[#00a8ff] rounded-xl backdrop-blur-sm hover:shadow-[0_0_30px_#00a8ff] transition-all duration-500 animate-float" style={{ transitionDelay: `${index * 200}ms`, transform: 'translateY(30px)', boxShadow: '0 0 15px rgba(0,168,255,0.3)', animationDelay: `${index}s` }}>
                <CardContent className="p-8">
                  <div className="text-6xl text-[#00a8ff] mb-4 font-bold" style={{ textShadow: '0 0 20px #00a8ff' }}>"</div>
                  <p className="text-white mb-6 italic text-lg" style={{ textShadow: '0 0 5px #00a8ff' }}>{testimonial.content}</p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[rgba(0,168,255,0.3)] flex items-center justify-center font-bold text-[#00a8ff] border-2 border-[#00a8ff]" style={{ boxShadow: '0 0 15px #00a8ff' }}>
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-bold text-white" style={{ textShadow: '0 0 8px #00a8ff' }}>{testimonial.name}</div>
                      <div className="text-sm text-[#00a8ff]" style={{ textShadow: '0 0 5px #00a8ff' }}>{testimonial.position}</div>
                      <div className="text-sm text-[#4cd3ff]" style={{ textShadow: '0 0 5px #4cd3ff' }}>{testimonial.company}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a2540] via-[#1a365d] to-[#0a2540]">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00a8ff] rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#4cd3ff] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>
        </div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="animate-on-scroll opacity-0" style={{ transform: 'translateY(30px)' }}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 led-text animate-led-pulse" style={{ textShadow: '0 0 20px #00a8ff, 0 0 40px #4cd3ff' }}>
              Ready to Start Your Project?
            </h2>
            <p className="text-xl mb-12 text-white max-w-2xl mx-auto" style={{ textShadow: '0 0 10px #00a8ff' }}>
              Let's discuss how we can help transform your ideas into reality
            </p>
            <Link to="/contact">
              <Button className="bg-[#00a8ff] text-[#0a2540] hover:bg-[#4cd3ff] font-bold px-12 py-8 text-xl transition-all duration-300 hover:scale-110 group" style={{ boxShadow: '0 0 30px #00a8ff' }}>
                <span className="flex items-center gap-3">
                  Get Started Today
                  <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform" />
                </span>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        .animate-on-scroll {
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .animate-on-scroll.visible {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </div>
  );
};